import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectActiveDocument,
  selectAdditionalEntityDocuments,
  selectAdditionalEntityDocumentsError,
  selectAllowedDocumentTypes,
  selectAllowedDocumentTypesError,
  selectAttributeTypes,
  selectDeletingDocumentError,
  selectDocumentThumbnailError,
  selectDocumentTypes,
  selectDocumentTypesError,
  selectEntityDocuments,
  selectEntityDocumentsError,
  selectLoadingAdditionalEntityDocuments,
  selectLoadingAllowedDocumentTypes,
  selectLoadingAttributeTypes,
  selectLoadingDocumentTypes,
  selectLoadingEntityDocuments,
  selectLoadingMimeTypes,
  selectLoadingRequiredDocumentTypes,
  selectMimeTypes,
  selectMimeTypesError,
  selectRequiredDocumentTypes,
  selectUpdatingDocumentError,
  selectUploadingDocument,
  selectUploadingEntityDocuments,
} from '../selectors/documents.selectors';
import {
  additionalEntityDocumentsError,
  allowedDocumentTypesError,
  attributeTypesError,
  cancelDocumentUpload,
  clearUploadingDocuments,
  deleteDocumentError,
  deleteDocumentSuccess,
  deletingDocument,
  documentTypesError,
  entityDocumentsError,
  loadingAttributeTypes,
  loadingDocumentTypes,
  loadingEntityDocuments,
  loadingMimeTypes,
  loadingRequiredDocumentTypes,
  mimeTypesError,
  removeAllDocuments as removeAllDocumentsAction,
  removeDocuments,
  requiredDocumentTypesError,
  storeActiveDocument,
  storeAdditionalEntityDocuments,
  storeAllowedDocumentTypes,
  storeAttributeTypes,
  storeDocument,
  storeDocumentDict,
  storeDocumentTypes,
  storeEntityDocuments,
  storeMimeTypes,
  storeRequiredDocumentTypes,
  updateDocumentError,
  updateDocumentSuccess,
  updatingDocument,
  uploadDocumentError,
  uploadDocumentProgress,
  uploadDocumentSuccess,
  uploadingDocument,
} from '../actions/documents.actions';
import { HttpClient, HttpEventType, HttpParams } from '@angular/common/http';
import environment from '@environments/environment';
import {
  Document,
  DocumentApiResponse,
  DocumentMimeType,
  DocumentMimeTypeApiResponse,
  DocumentType,
  DocumentTypeApiResponse,
  InProgressDocument,
  RequiredDocumentType,
  RequiredDocumentTypeApiResponse,
} from '../models/document-type';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import * as SubmissionActions from '@submissions/v1/redux/submission.actions';
import { IrisService } from '@shared/services/iris.service';
import { selectActiveSubmissionId } from '@submissions/v1/redux/submission.selectors';
import { extractBlobAndFilename } from '@shared/utils/download.util';

@Injectable({
  providedIn: 'root',
})
export class DocumentsService {
  private readonly _http = inject(HttpClient);
  private readonly _store = inject(Store);
  private readonly _irisService = inject(IrisService);

  private uploadSubscriptions = new Map<string, Subscription>();
  private uploadSubscriptionsSubject = new BehaviorSubject<
    Map<string, Subscription>
  >(this.uploadSubscriptions);

  private displayModalSubject = new BehaviorSubject<{
    display: boolean;
    nextUrl?: string;
  } | null>(null);
  private documentsSubject = new BehaviorSubject<Document[]>([]);
  private documentTypesSubject = new BehaviorSubject<DocumentType[]>([]);
  private requiredDocumentTypesSubject = new BehaviorSubject<
    RequiredDocumentType[]
  >([]);
  private loadedEntityDocuments: any = {};
  displayModal$ = this.displayModalSubject;
  activeDocument$ = this._store.select(selectActiveDocument);

  get uploadSubscriptions$(): Observable<Map<string, Subscription>> {
    return this.uploadSubscriptionsSubject.asObservable();
  }

  /**
   * Get a list of allowed document types for a workspace.
   */
  getAllowedDocumentTypes(workspaceId: string) {
    return combineLatest([
      this._store.select(selectAllowedDocumentTypes(workspaceId)),
      this._store.select(selectLoadingAllowedDocumentTypes(workspaceId)),
    ]).pipe(
      take(1),
      mergeMap(([documentTypes, loading]) => {
        if (!documentTypes?.length && !loading) {
          this.loadAllowedDocumentTypes(workspaceId);
        }

        return this._store
          .select(selectAllowedDocumentTypes(workspaceId))
          .pipe(filter((types) => !!types));
      }),
    );
  }

  /**
   * Get the loading state of allowed document types for a workspace.
   */
  getLoadingAllowedDocumentTypes(workspaceId: string) {
    return this._store
      .select(selectLoadingAllowedDocumentTypes(workspaceId))
      .pipe(map((loading) => !!loading));
  }

  /**
   * Get the error state of allowed document types for a workspace.
   */
  getAllowedDocumentTypesError(workspaceId: string) {
    return this._store.select(selectAllowedDocumentTypesError(workspaceId));
  }

  /**
   * Get a list of required document types for a workspace type.
   */
  getRequiredDocumentTypes(
    workspaceTypeId: string,
  ): Observable<RequiredDocumentType[]> {
    return combineLatest([
      this._store.select(selectRequiredDocumentTypes(workspaceTypeId)),
      this._store.select(selectLoadingRequiredDocumentTypes(workspaceTypeId)),
    ]).pipe(
      take(1),
      mergeMap(([documentTypes, loading]) => {
        if (!documentTypes?.length && !loading) {
          this.loadRequiredDocumentTypes(workspaceTypeId);
        }

        return this._store
          .select(selectRequiredDocumentTypes(workspaceTypeId))
          .pipe(filter((types) => !!types));
      }),
    );
  }

  /**
   * Get the loading state of required document types for a workspace type.
   */
  getLoadingRequiredDocumentTypes(workspaceTypeId: string) {
    return this._store
      .select(selectLoadingDocumentTypes(workspaceTypeId))
      .pipe(map((loading) => !!loading));
  }

  /**
   * Get the error state of required document types for a workspace type.
   */
  getRequiredDocumentTypesError(workspaceTypeId: string) {
    return this._store.select(selectRequiredDocumentTypes(workspaceTypeId));
  }

  /**
   * Get a list of document types. To load all document types, do not pass a typeId.
   */
  getDocumentTypes(
    typeId?: string,
    entityType?: string,
  ): Observable<DocumentType[]> {
    return this.getLoadingDocumentTypes(typeId).pipe(
      take(1),
      mergeMap((loading) => {
        if (!loading) {
          this.loadDocumentTypes(typeId, entityType);
        }

        return this._store
          .select(selectDocumentTypes(typeId))
          .pipe(filter((types) => !!types));
      }),
    );
  }

  /**
   * Get a list of document types. To load all document types, do not pass a typeId.
   */
  getDocumentTypesV2(typeId?: string): Observable<DocumentType[]> {
    return this.getLoadingDocumentTypes(typeId).pipe(
      take(1),
      mergeMap((loading) => {
        if (!loading) {
          this.loadDocumentTypesV2(typeId);
        }

        return this._store
          .select(selectDocumentTypes(typeId))
          .pipe(filter((types) => !!types));
      }),
    );
  }

  /**
   * Get the loading state of document types. To get the loading state of all document types, do not pass a typeId.
   */
  getLoadingDocumentTypes(typeId?: string): Observable<boolean> {
    return this._store
      .select(selectLoadingDocumentTypes(typeId))
      .pipe(map((loading) => !!loading));
  }

  /**
   * Get the error state of document types. To get the error state of all document types, do not pass a typeId.
   */
  getDocumentTypesError(typeId?: string) {
    return this._store.select(selectDocumentTypesError(typeId));
  }

  /**
   * Get a list of mime types.
   */
  getMimeTypes(): Observable<DocumentMimeType[]> {
    return combineLatest([
      this._store.select(selectMimeTypes),
      this._store.select(selectLoadingMimeTypes),
    ]).pipe(
      take(1),
      mergeMap(([mimeTypes, loading]) => {
        if (!mimeTypes?.length && !loading) {
          this.loadMimeTypes();
        }

        return this._store
          .select(selectMimeTypes)
          .pipe(filter((types) => !!types));
      }),
    );
  }

  /**
   * Get the loading state of mime types.
   */
  getLoadingMimeTypes(): Observable<boolean> {
    return this._store
      .select(selectLoadingMimeTypes)
      .pipe(map((loading) => !!loading));
  }

  /**
   * Get the error state of mime types.
   */
  getMimeTypesError() {
    return this._store.select(selectMimeTypesError);
  }

  /**
   * Get the loading state of a document upload.
   */
  getUploadingDocument(entityId: string, documentId: string) {
    return this._store.select(selectUploadingDocument(entityId, documentId));
  }

  /**
   * Get all of the uploading documents for an entity.
   */
  getUploadingEntityDocuments(entityId: string) {
    return this._store.select(selectUploadingEntityDocuments(entityId));
  }

  /**
   * Get the loading state of a document update.
   */
  getUpdatingDocument(documentId: string) {
    return this._store.select(selectUpdatingDocumentError(documentId));
  }

  /**
   * Get the error state of a document update.
   */
  getUpdatingDocumentError(documentId: string) {
    return this._store.select(selectUpdatingDocumentError(documentId));
  }

  /**
   * Get the loading state of a document deletion.
   */
  getDeletingDocument(documentId: string) {
    return this._store.select(selectDeletingDocumentError(documentId));
  }

  /**
   * Get the error state of a document deletion.
   */
  getDeleteDocumentError(documentId: string) {
    return this._store.select(selectDocumentThumbnailError(documentId));
  }

  /**
   * Delete a document.
   */
  deleteDocument(documentId: string, entityId: string, entityType: string) {
    this._store.dispatch(deletingDocument({ documentId }));

    this._http
      .delete(`${environment.irisApi}/document/${documentId}/`)
      .pipe(
        catchError((error) => {
          this._store.dispatch(deleteDocumentError({ error, documentId }));

          return EMPTY;
        }),
      )
      .subscribe(() => {
        this._store.dispatch(
          deleteDocumentSuccess({ documentId, entityType, entityId }),
        );
        this.loadEntityDocuments(entityType, entityId);
      });
  }

  uploadRequestedDocument(
    file: File,
    documentTypeId: string,
    documentTypeName: string,
    documentName: string,
    threadId: string,
    _: string,
  ) {
    this._store
      .select(selectActiveSubmissionId)
      .pipe(
        take(1), // Ensures the observable completes after the first value is emitted
      )
      .subscribe((submissionId) => {
        const formData = new FormData();
        const entityId = submissionId || '';
        const uuid = threadId;

        formData.append('file', file);
        formData.append('data', '{}');
        formData.append('document_type_id', documentTypeId);
        formData.append('name', documentName);

        this._store.dispatch(
          uploadingDocument({
            additional: false,
            documentName,
            documentTypeId,
            documentTypeName,
            entityId,
            filename: file.name,
            uuid,
          }),
        );

        const subscription = this._http
          .post(
            `${environment.irisApi}/workspace/${submissionId}/request-document/${threadId}/respond-new/`,
            formData,
            {
              observe: 'events',
              reportProgress: true,
            },
          )
          .pipe(
            catchError((e) => {
              this._store.dispatch(
                uploadDocumentError({
                  entityId,
                  error: e,
                  uuid,
                }),
              );
              this.uploadSubscriptions.delete(uuid);
              return EMPTY;
            }),
          )
          .subscribe((event) => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                if (event.total) {
                  this._store.dispatch(
                    uploadDocumentProgress({
                      entityId,
                      progress: Math.round((100 * event.loaded) / event.total),
                      uuid,
                    }),
                  );
                } else {
                  this._store.dispatch(
                    uploadDocumentProgress({
                      entityId,
                      progress: 100,
                      uuid,
                    }),
                  );
                }

                break;
              case HttpEventType.Response:
                this._store.dispatch(
                  uploadDocumentSuccess({
                    document: event.body as Document,
                    entityId,
                    uuid,
                  }),
                );

                this._store.dispatch(
                  storeDocument({
                    document: event.body as Document,
                    workspaceId: entityId || '',
                  }),
                );

                this._store.dispatch(SubmissionActions.closeActiveThread());
                this.uploadSubscriptions.delete(uuid);

                this.loadEntityDocuments('workspace', entityId, false, true);

                break;
            }
          });
        this.uploadSubscriptions.set(uuid, subscription);
      });
  }

  /**
   * Upload a document.
   */
  uploadDocument(
    uuid: string,
    file: File,
    documentTypeId: string,
    documentTypeName: string,
    documentName: string,
    entityId: string,
    entityType: string,
    additional?: boolean,
    questionId?: string,
    isResponse?: boolean,
    _?: boolean,
  ): Observable<unknown> {
    return this._store.select(selectUploadingDocument(entityId, uuid)).pipe(
      take(1),
      switchMap((uploading) => {
        if (uploading) {
          return EMPTY;
        }

        this._store.dispatch(
          uploadingDocument({
            uuid,
            entityId,
            filename: file.name,
            additional: !!additional,
            documentTypeId,
            questionId,
            documentName,
            documentTypeName,
          }),
        );

        let params = new HttpParams();
        if (additional) {
          params = params.set('additional', 'true');
        }

        const data = new FormData();
        data.append('file', file);
        data.append('entity_id', entityId);
        data.append('entity_type', entityType);
        data.append('document_type_id', documentTypeId);
        data.append('name', documentName);
        if (questionId) {
          data.append('question_id', questionId);
        }
        if (isResponse) {
          data.append('is_response', 'true');
        }

        const upload$ = this._http
          .post<Document>(`${environment.irisApi}/document/`, data, {
            reportProgress: true,
            observe: 'events',
            params,
          })
          .pipe(
            tap((event) => {
              switch (event.type) {
                case HttpEventType.UploadProgress:
                  if (event.total) {
                    this._store.dispatch(
                      uploadDocumentProgress({
                        uuid,
                        entityId,
                        progress: Math.round(
                          (100 * event.loaded) / event.total,
                        ),
                      }),
                    );
                  } else {
                    this._store.dispatch(
                      uploadDocumentProgress({
                        uuid,
                        entityId,
                        progress: 100,
                      }),
                    );
                  }
                  break;

                case HttpEventType.Response:
                  this._store.dispatch(
                    uploadDocumentSuccess({
                      uuid,
                      entityId,
                      document: event.body,
                    }),
                  );
                  this.uploadSubscriptions.delete(uuid);
                  break;
              }
            }),
            catchError((e) => {
              this._store.dispatch(
                uploadDocumentError({ error: e, uuid, entityId }),
              );
              this.uploadSubscriptions.delete(uuid);
              return throwError(() => e);
            }),
            shareReplay(1),
          );

        const subscription = upload$.subscribe();
        this.uploadSubscriptions.set(uuid, subscription);

        return upload$;
      }),
    );
  }

  /**
   * Cancel a document upload.
   */
  cancelUpload(entityId: string, uuid: string) {
    const abortController = this.uploadSubscriptions.get(uuid);
    if (abortController) {
      abortController.unsubscribe();
      this.uploadSubscriptions.delete(uuid);
      this._store.dispatch(cancelDocumentUpload({ uuid, entityId }));
    }
  }

  /**
   * Cancel all pending uploads for an entity.
   */
  cancelAllEntityUploads(entityId: string) {
    this._store
      .select(selectUploadingEntityDocuments(entityId))
      .pipe(take(1))
      .subscribe((documents) => {
        documents.forEach((document) => {
          this.cancelUpload(entityId, document.id);
        });
      });
  }

  /**
   * Update a document.
   */
  updateDocument(document: Document) {
    this._store.dispatch(updatingDocument({ documentId: document.id }));

    const payload: any = {};
    if (document.document_type) {
      payload.document_type_id = document.document_type.id?.replace(
        /^optional-/,
        '',
      );
    }
    if (document.attribute_type) {
      payload.attribute_type_id = document.attribute_type.id;
    }
    if (document.name && document.name !== document.title) {
      payload.name = document.name;
    }
    if (document.sites) {
      payload.sites = document.sites;
    }
    if (document.wcg_name) {
      payload.wcg_name = document.wcg_name;
    }
    this._http
      .put<Document>(`${environment.irisApi}/document/${document.id}/`, payload)
      .pipe(
        catchError((e) => {
          this._store.dispatch(
            updateDocumentError({ error: e, documentId: document.id }),
          );

          return EMPTY;
        }),
      )
      .subscribe((document: Document) => {
        this._store.dispatch(
          updateDocumentSuccess({ document, documentId: document.id }),
        );
        this.loadedEntityDocuments = false;
        this.documentsSubject.next([]);
        this.loadEntityDocuments(
          document.entity_type,
          document.entity_id,
          document.is_additional,
          true,
        );
      });
  }

  /**
   * Update the completion status of a document.
   */
  updateDocumentCompletion(
    documentId: string,
    entityId: string,
    entityType: string,
    complete: boolean,
  ): Observable<Document | void> {
    this._store.dispatch(updatingDocument({ documentId }));

    return this._http
      .put<Document>(`${environment.irisApi}/document/${documentId}/mark/`, {
        is_complete: complete,
        entity_id: entityId,
        entity_type: entityType,
      })
      .pipe(
        catchError((e) => {
          this._store.dispatch(updateDocumentError({ error: e, documentId }));
          return EMPTY; // Or return of(undefined) if you want to emit a value
        }),
        tap((document: Document) => {
          if (document) {
            this._store.dispatch(
              updateDocumentSuccess({ document, documentId }),
            );
            this.loadedEntityDocuments = false;
            this.documentsSubject.next([]);
            this.loadEntityDocuments(
              document.entity_type,
              document.entity_id,
              document.is_additional,
            );
          }
        }),
      );
  }

  /**
   * Get a list of documents for an entity.
   */
  getDocuments(
    entityId: string,
    entityType: string,
  ): Observable<Array<Document | InProgressDocument>> {
    return combineLatest([
      this._store.select(selectEntityDocuments(entityId, entityType)),
      this._store.select(selectUploadingEntityDocuments(entityId)),
      this._store.select(selectLoadingEntityDocuments(entityId, entityType)),
    ]).pipe(
      take(1),
      mergeMap(([documents, uploadingDocuments, loading]) => {
        if (!documents?.length && !uploadingDocuments?.length && !loading) {
          this.loadEntityDocuments(entityType, entityId);
        }

        return combineLatest([
          this._store.select(selectUploadingEntityDocuments(entityId)),
          this._store.select(selectEntityDocuments(entityId, entityType)),
        ]).pipe(
          map(([uploading, docs]) => {
            return [...uploading, ...(docs ?? [])];
          }),
        );
      }),
    );
  }

  /**
   * Get the error state of documents for an entity.
   */
  getDocumentsError(entityId: string, entityType: string) {
    return this._store.select(selectEntityDocumentsError(entityId, entityType));
  }

  /**
   * Get the loading state of additional documents for an entity.
   */
  getLoadingAdditionalDocuments(entityId: string, entityType: string) {
    return this._store
      .select(selectLoadingAdditionalEntityDocuments(entityId, entityType))
      .pipe(map((loading) => !!loading));
  }

  /**
   * Get a list of additional documents for an entity.
   */
  getAdditionalDocuments(entityId: string, entityType: string) {
    return combineLatest([
      this._store.select(selectAdditionalEntityDocuments(entityId, entityType)),
      this._store.select(
        selectLoadingAdditionalEntityDocuments(entityId, entityType),
      ),
    ]).pipe(
      take(1),
      mergeMap(([documents, loading]) => {
        if (!documents?.length && !loading) {
          this.loadEntityDocuments(entityType, entityId, true);
        }

        return this._store.select(
          selectAdditionalEntityDocuments(entityId, entityType),
        );
      }),
    );
  }

  /**
   * Get the error state of additional documents for an entity.
   */
  getAdditionalDocumentsError(entityId: string, entityType: string) {
    return this._store.select(
      selectAdditionalEntityDocumentsError(entityId, entityType),
    );
  }

  /**
   * Get the loading state of documents for an entity.
   */
  getLoadingDocuments(entityId: string, entityType: string) {
    return this._store
      .select(selectLoadingEntityDocuments(entityId, entityType))
      .pipe(map((loading) => !!loading));
  }

  /**
   * Set the active document.
   */
  setActiveDocument(document: Document) {
    return this._store.dispatch(storeActiveDocument({ document }));
  }

  /**
   * Get a list of attribute types.
   */
  getAttributeTypes(type: string): Observable<any> {
    if (!type) {
      return of([]);
    }
    return combineLatest([
      this._store.select(selectAttributeTypes),
      this._store.select(selectLoadingAttributeTypes),
    ]).pipe(
      take(1),
      mergeMap(([attributeTypes, loading]) => {
        if (!attributeTypes?.length && !loading) {
          this._loadAttributeTypes(type);
        }

        return this._store
          .select(selectAttributeTypes)
          .pipe(filter((types) => !!types));
      }),
    );
  }

  /**
   * Gets the blob and filename of a documentId
   * @param documentId The ID of the document to retrieve.
   * @returns An observable that emits the blob and filename of the document.
   */
  downloadDocument(documentId: string) {
    return this._http
      .get<Blob>(`${environment.irisApi}/document/${documentId}/`, {
        responseType: 'blob' as 'json',
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return extractBlobAndFilename(response);
        }),
      );
  }

  /**
   * Load documents for an entity.
   */
  loadEntityDocuments(
    entityType: string,
    entityId: string,
    additional?: boolean,
    reload?: boolean,
  ): BehaviorSubject<Document[]> | null {
    if (!entityType || !entityId) {
      return null;
    }

    const key = `${entityType}-${entityId}-${additional ? 'additional' : 'main'}`;
    if (this.loadedEntityDocuments[key] && !reload) {
      this.documentsSubject.next(this.loadedEntityDocuments[key]);
      if (additional) {
        this._store.dispatch(
          storeAdditionalEntityDocuments({
            entityType,
            entityId,
            documents: this.loadedEntityDocuments[key],
          }),
        );
      } else {
        this._store.dispatch(
          storeEntityDocuments({
            entityType,
            entityId,
            documents: this.loadedEntityDocuments[key],
          }),
        );
      }
      return this.documentsSubject;
    }
    this._store.dispatch(loadingEntityDocuments({ entityType, entityId }));

    let params = new HttpParams();

    if (additional) {
      params = params.set('additional', 'true');
    }
    params = params.set('page_size', 100);

    forkJoin({
      documents: this._http
        .get<DocumentApiResponse>(
          `${environment.irisApi}/document/${entityType}/${entityId}/`,
          { params },
        )
        .pipe(
          catchError((e) => {
            if (additional) {
              this._store.dispatch(
                additionalEntityDocumentsError({
                  entityType,
                  entityId,
                  error: e,
                }),
              );
            } else {
              this._store.dispatch(
                entityDocumentsError({ entityType, entityId, error: e }),
              );
            }

            return EMPTY;
          }),
        ),
      requestedDocuments: this.getRequestedDocuments(entityId),
    }).subscribe(({ documents, requestedDocuments }) => {
      const requestedDocumentDict: any = {};

      requestedDocuments.data.forEach((docMessage: any) => {
        if (!requestedDocumentDict[docMessage.entity_id]) {
          requestedDocumentDict[docMessage.entity_id] = [];
        }
        requestedDocumentDict[docMessage.entity_id].push(docMessage);
      });

      documents.data.forEach((doc) => {
        if (requestedDocumentDict[doc.id]) {
          doc.is_resolved = requestedDocumentDict[doc.id].every(
            (docMessage: any) => docMessage.is_resolved,
          );
          doc.messages = requestedDocumentDict[doc.id].length;
        }
      });

      const documentsDict: any = {};
      documents.data.forEach((doc) => {
        documentsDict[doc.id] = {
          entityId: doc.id,
          title: doc.title,
          workspaceId: doc.entity_id,
          data: doc.data,
          documentType: doc.document_type,
        };
      });

      this._store.dispatch(storeDocumentDict({ documentDict: documentsDict }));
      this.documentsSubject.next(documents.data);
      if (additional) {
        this._store.dispatch(
          storeAdditionalEntityDocuments({
            entityType,
            entityId,
            documents: documents.data,
          }),
        );
      } else {
        this._store.dispatch(
          storeEntityDocuments({
            entityType,
            entityId,
            documents: documents.data,
          }),
        );
      }
    });
    return this.documentsSubject;
  }

  triggerUploadInProgressModal(nextUrl?: string): void {
    this.displayModalSubject.next({
      display: true,
      nextUrl,
    });
  }

  dismissUploadInProgressModal(): void {
    this.displayModalSubject.next(null);
  }

  removeUploadingDocuments() {
    this._store.dispatch(clearUploadingDocuments());
  }

  requestDocument(request: {
    text: string;
    document_type_id: string;
    workspace_id: string;
  }) {
    this._http
      .post<any>(
        `${environment.irisApi}/workspace/${request.workspace_id}/request-document/`,
        request,
      )
      .pipe(
        map(() => {
          this._store.dispatch(
            SubmissionActions.requestDocumentThreadCreationSuccess(),
          );
        }),
      )
      .subscribe();
  }

  getRequestedDocuments(workspaceId: string) {
    return this._http.get<any>(
      `${environment.irisApi}/workspace/${workspaceId}/request-document/?workspace_id=${workspaceId}&page_size=100`,
    );
  }

  /**
   * Remove documents from an entity. This does not call the API, it just updates the state.
   */
  removeDocuments(
    entityId: string,
    entityType: string,
    documentIds: string[],
    removeAllDocuments?: boolean,
  ) {
    if (removeAllDocuments) {
      this._store.dispatch(removeAllDocumentsAction({ entityType, entityId }));
    } else {
      this._store.dispatch(
        removeDocuments({ entityType, entityId, documentIds }),
      );
    }
    setTimeout(() => {
      this.loadEntityDocuments(entityType, entityId);
    }, 0);
  }

  /**
   * Get a document's thumnail image as a data URL.
   */
  getDocumentThumbnail(documentId: string): Observable<string> {
    return this._http
      .get<Blob>(
        `${environment.irisApi}/document/${documentId}/thumbnail/download/`,
        { responseType: 'blob' as 'json' },
      )
      .pipe(
        map((blob) => {
          if (blob && blob.size > 0) {
            return URL.createObjectURL(blob);
          }

          return '';
        }),
      );
  }
  removeSubmission(
    workspace_id: string,
    delete_reason: string,
    isInternal: boolean,
  ) {
    const url = `${environment.irisApi}/workspace/${isInternal ? 'admin/' : ''}${workspace_id}/delete/`;
    return this._http.put(url, {
      reason_for_delete: delete_reason,
    });
  }
  public fetchDocumentTypes(
    typeId?: string,
    entityType?: string,
  ): Observable<void> {
    return new Observable<void>((observer) => {
      this.loadDocumentTypes(typeId, entityType);
      observer.next();
      observer.complete();
    });
  }

  /**
   * Load Attribute Types.
   */
  private _loadAttributeTypes(type: string) {
    this._store.dispatch(loadingAttributeTypes());

    this._irisService
      .getAttributeTypes(type)
      .pipe(
        catchError((e) => {
          this._store.dispatch(attributeTypesError({ error: e }));

          return EMPTY;
        }),
      )
      .subscribe((attributeTypes) =>
        this._store.dispatch(
          storeAttributeTypes({ attributeTypes: attributeTypes?.data }),
        ),
      );
  }

  /**
   * Load mime types.
   */
  private loadMimeTypes() {
    this._store.dispatch(loadingMimeTypes());

    this._http
      .get<DocumentMimeTypeApiResponse>(
        `${environment.irisApi}/document/mime_types/`,
      )
      .pipe(
        catchError((e) => {
          this._store.dispatch(mimeTypesError({ error: e }));

          return EMPTY;
        }),
      )
      .subscribe((mimeTypes) =>
        this._store.dispatch(storeMimeTypes({ mimeTypes: mimeTypes.data })),
      );
  }

  /**
   * Load allowed document types for a workspace.
   */
  private loadAllowedDocumentTypes(workspaceId: string) {
    const params = new HttpParams().set('workspace_id', workspaceId);

    this._http
      .get<DocumentTypeApiResponse>(
        `${environment.irisApi}/document/allowed-document-type/`,
        { params },
      )
      .pipe(
        catchError((e) => {
          this._store.dispatch(
            allowedDocumentTypesError({ workspaceId, error: e }),
          );

          return EMPTY;
        }),
      )
      .subscribe((documentTypes) =>
        this._store.dispatch(
          storeAllowedDocumentTypes({
            workspaceId,
            documentTypes: documentTypes.data,
          }),
        ),
      );
  }

  /**
   * Load required document types for a workspace type.
   */
  private loadRequiredDocumentTypes(
    workspaceTypeId: string,
  ): Observable<RequiredDocumentType[]> {
    this._store.dispatch(loadingRequiredDocumentTypes({ workspaceTypeId }));

    const params = new HttpParams().set('workspace_type_id', workspaceTypeId);

    this._http
      .get<RequiredDocumentTypeApiResponse>(
        `${environment.irisApi}/document/required_document_type/`,
        {
          params,
        },
      )
      .pipe(
        catchError((e) => {
          this._store.dispatch(
            requiredDocumentTypesError({ workspaceTypeId, error: e }),
          );

          return EMPTY;
        }),
      )
      .subscribe((documentTypes) => {
        this.requiredDocumentTypesSubject.next(documentTypes.data);

        this._store.dispatch(
          storeRequiredDocumentTypes({
            documentTypes: documentTypes.data,
            workspaceTypeId,
          }),
        );
      });
    return this.requiredDocumentTypesSubject.asObservable();
  }

  /**
   * Load document types.
   */
  private loadDocumentTypes(
    entityTypeId?: string,
    entityType?: string,
  ): BehaviorSubject<DocumentType[]> {
    this._store.dispatch(loadingDocumentTypes({ entityTypeId }));

    let params = new HttpParams();
    if (entityTypeId) {
      params = params.set('workspace_type_id', entityTypeId);
    }
    if (entityType) {
      params = params.set('entity_type', entityType);
    }

    this._http
      .get<DocumentTypeApiResponse>(`${environment.irisApi}/document/type/`, {
        params,
      })
      .pipe(
        catchError((e) => {
          this._store.dispatch(documentTypesError({ entityTypeId, error: e }));

          return EMPTY;
        }),
      )
      .subscribe((documentTypes) => {
        this.documentTypesSubject.next(documentTypes.data);

        this._store.dispatch(
          storeDocumentTypes({
            documentTypes: documentTypes.data,
            entityTypeId,
          }),
        );
      });
    return this.documentTypesSubject;
  }

  /**
   * Load document types for v2
   */
  private loadDocumentTypesV2(
    entityTypeId?: string,
  ): BehaviorSubject<DocumentType[]> {
    this._store.dispatch(loadingDocumentTypes({ entityTypeId }));

    let params = new HttpParams();
    if (entityTypeId) {
      params = params.set('workspace_type_id', entityTypeId);
    }

    this._http
      .get<DocumentTypeApiResponse>(
        `${environment.irisApi}/v2/document/type/`,
        {
          params,
        },
      )
      .pipe(
        catchError((e) => {
          this._store.dispatch(documentTypesError({ entityTypeId, error: e }));

          return EMPTY;
        }),
      )
      .subscribe((documentTypes) => {
        this.documentTypesSubject.next(documentTypes.data);

        this._store.dispatch(
          storeDocumentTypes({
            documentTypes: documentTypes.data,
            entityTypeId,
          }),
        );
      });
    return this.documentTypesSubject;
  }
}
